import { adicionarLogo } from "../../services/pdf-logo";
import logo from "../../images/s.png";
import jsPDF from "jspdf";
import autoTable, { Styles, FontStyle, CellHookData } from "jspdf-autotable";
import * as XLSX from "xlsx";
import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { obterIdTenent } from "../../services/seguranca";
import { useAppSelector } from "../../store/hooks";

export interface Coluna {
  titulo: string;
  propriedade: string;
  calculado?: (data: any) => string | number;
  alinhamento?: 'left' | 'center' | 'right';
  largura?: number;
  estiloCondicional?: (valor: any, rowData: any) => Partial<Styles> | null;
}

export interface Totalizador {
  coluna: string;
  label: string;
  calculo: (dados: any[]) => string | number;
}

export interface BandaDetalhe {
  nome: string;
  colunas: Coluna[];
  propriedadeDados: string;
  totalizadores?: Totalizador[];
}

export interface ConfiguracaoBanda {
  mestre: {
    colunas: Coluna[];
    totalizadores?: Totalizador[];
  };
  detalhes?: BandaDetalhe[];
}

// Configuracao opcional para renderizar o relatorio agrupado por um campo
// (ex.: banco). Cada grupo vira uma secao com cabecalho, tabela e subtotal.
export interface AgrupamentoRelatorio {
  campoTitulo: string;
  tituloVazio: string;
  totalizadores?: Totalizador[];
}

export type FormatoRelatorio = 'pdf' | 'xlsx';

interface RelatorioProps {
  titulo: string;
  trigger: number;
  usuario: string;
  empresa: string;
  configuracaoBanda: ConfiguracaoBanda;
  rodape?: string[];
  subtitulo?: string;
  fonteDeDados: any[];
  modoCompacto?: boolean;
  formato?: FormatoRelatorio;
  agrupamento?: AgrupamentoRelatorio;
}

const getNestedProperty = (obj: any, path: string): any => {
  try {
    return path.split('.').reduce((current, key) =>
      current && current[key] !== undefined ? current[key] : null, obj);
  } catch (error) {
    return null;
  }
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value.toLocaleString('pt-BR');
  return String(value);
};

// Agrupa os registros por um campo. Chave vazia/nula cai no grupo "tituloVazio".
// Ordena alfabeticamente, com o grupo vazio sempre por ultimo.
const agruparPorCampo = (
  dados: any[],
  campo: string,
  tituloVazio: string
): { titulo: string; itens: any[] }[] => {
  const mapa = new Map<string, any[]>();
  dados.forEach((item) => {
    const bruto = getNestedProperty(item, campo);
    const chave =
      bruto !== null && bruto !== undefined && String(bruto).trim() !== ''
        ? String(bruto)
        : tituloVazio;
    if (!mapa.has(chave)) mapa.set(chave, []);
    mapa.get(chave)!.push(item);
  });

  return Array.from(mapa.keys())
    .sort((a, b) => {
      if (a === tituloVazio) return 1;
      if (b === tituloVazio) return -1;
      return a.localeCompare(b, 'pt-BR');
    })
    .map((titulo) => ({ titulo, itens: mapa.get(titulo)! }));
};

export const Relatorio: React.FC<RelatorioProps> = ({
  fonteDeDados,
  configuracaoBanda,
  trigger,
  titulo,
  subtitulo,
  usuario,
  empresa,
  rodape = [],
  modoCompacto = false,
  formato = 'pdf',
  agrupamento,
}) => {
  const token = useAppSelector(state => state.token);

  const obterLogoDaEmpresa = async () => {
    try {
      const response = await api.get(`Empresa/ObterURLLogo/${obterIdTenent(token)}`);
      return response.data;
    } catch (error) {
      return "https://locasmartpro.com.br/lovable-uploads/1a30b1bd-1b9b-44d2-81b1-9dd18a301a5b.png";
    }
  };

  const gerarRelatorioPDF = async () => {
    const pdf = new jsPDF({
      orientation: modoCompacto ? "portrait" : "landscape",
      unit: "mm",
      format: "a4"
    });

    const img = new Image();
    const url = await obterLogoDaEmpresa();

    const dataEmissao = new Date().toLocaleDateString('pt-BR');
    const paleta = {
      primaria: "#1A202C",
      secundaria: "#64748B",
      borda: "#E2E8F0",
      fundo: "#FFFFFF",
      destaque: "#1E3A5F",
      destaqueClaro: "#2D5F8A",
      fundoAlternado: "#F8FAFC",
      fundoTitulo: "#F1F5F9",
      fundoMestre: "#F8FAFC",
      bordaMestre: "#CBD5E1",
      totalFundo: "#1E3A5F",
      totalTexto: "#FFFFFF",
    };

    const dimensoes = {
      larguraPagina: pdf.internal.pageSize.width,
      alturaPagina: pdf.internal.pageSize.height,
      margem: modoCompacto ? 8 : 10,
      logo: { largura: modoCompacto ? 34 : 40, altura: modoCompacto ? 18 : 22 },
      espacamentoSeccao: modoCompacto ? 1 : 2,
      margemDetalhe: modoCompacto ? 5 : 10,
      espacamentoBordas: modoCompacto ? 8 : 10,
      alturaLinha: modoCompacto ? 4 : 5,
      alturaCabecalho: modoCompacto ? 5 : 7,
      alturaRegistroMestre: modoCompacto ? 8 : 10,
    };

    const areaTextoLargura = dimensoes.larguraPagina - (2 * dimensoes.margem) - dimensoes.logo.largura - 10;

    // Rodape para todas as paginas
    pdf.setPage(1);
    (pdf as any).autoTableSetDefaults({
      didDrawPage: (data: any) => {
        const rodapeY = dimensoes.alturaPagina - (modoCompacto ? 14 : 18);

        pdf.setDrawColor(paleta.borda);
        pdf.setLineWidth(0.3);
        pdf.line(
          dimensoes.espacamentoBordas,
          rodapeY,
          dimensoes.larguraPagina - dimensoes.espacamentoBordas,
          rodapeY
        );

        pdf.setFont("helvetica", "normal");
        pdf.setFontSize(modoCompacto ? 7 : 8);
        pdf.setTextColor(paleta.secundaria);
        rodape.forEach((texto, idx) => {
          pdf.text(
            texto,
            dimensoes.espacamentoBordas,
            rodapeY + 4 + (idx * (modoCompacto ? 3 : 3.5)),
          );
        });

        const pageCount = (pdf as any).internal.getNumberOfPages();
        const currentPage = data.pageNumber;
        pdf.setFontSize(modoCompacto ? 7 : 8);
        pdf.text(
          `${currentPage} / ${pageCount}`,
          dimensoes.larguraPagina - dimensoes.espacamentoBordas - 12,
          rodapeY + 4
        );
      },
    });

    const gerarConteudoPDF = (imgCarregada: HTMLImageElement | null) => {
      // === CABECALHO ===
      if (imgCarregada) {
        try {
          adicionarLogo(pdf, imgCarregada, {
            x: dimensoes.larguraPagina - dimensoes.logo.largura - dimensoes.margem,
            y: dimensoes.margem,
            largura: dimensoes.logo.largura,
            altura: dimensoes.logo.altura,
            alinhar: "direita",
          });
        } catch (e) {
          console.warn("Nao foi possivel adicionar a logo ao PDF:", e);
        }
      }

      // Titulo
      pdf.setFont("helvetica", "bold");
      pdf.setTextColor(paleta.primaria);
      pdf.setFontSize(modoCompacto ? 14 : 16);
      pdf.text(titulo.toUpperCase(), dimensoes.margem, dimensoes.margem + (modoCompacto ? 6 : 7));

      // Nome da empresa
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(modoCompacto ? 9 : 10);
      pdf.setTextColor(paleta.secundaria);
      pdf.text(empresa.toUpperCase(), dimensoes.margem, dimensoes.margem + (modoCompacto ? 11 : 12));

      // Subtitulo (filtros) com quebra de linha para nao sobrepor a logo
      let posY = dimensoes.margem + (modoCompacto ? 15 : 17);
      if (subtitulo) {
        pdf.setFontSize(modoCompacto ? 7 : 8);
        pdf.setTextColor(paleta.secundaria);

        const filtros = subtitulo.split(' | ');
        filtros.forEach((filtro) => {
          const linhas = pdf.splitTextToSize(filtro.trim(), areaTextoLargura);
          linhas.forEach((linha: string) => {
            pdf.text(linha, dimensoes.margem, posY);
            posY += modoCompacto ? 3 : 3.5;
          });
        });
      }

      // Emitido por / Data
      pdf.setFontSize(modoCompacto ? 6.5 : 7.5);
      pdf.setTextColor(paleta.secundaria);
      pdf.text(`Emitido por ${usuario}  |  ${dataEmissao}`, dimensoes.margem, posY + 1);

      // Linha separadora do cabecalho
      posY += modoCompacto ? 4 : 5;
      pdf.setDrawColor(paleta.destaque);
      pdf.setLineWidth(0.5);
      pdf.line(dimensoes.margem, posY, dimensoes.larguraPagina - dimensoes.margem, posY);
      posY += modoCompacto ? 3 : 4;

      // === ESTILOS DE TABELA ===
      const estilosTabela: Partial<Styles> = {
        fontSize: modoCompacto ? 6.5 : 8,
        cellPadding: modoCompacto ? 1.2 : 2,
        lineWidth: 0.1,
        lineColor: paleta.borda,
        fillColor: paleta.fundo,
        textColor: paleta.primaria,
        halign: 'left',
      };

      const estilosDetalhe: Partial<Styles> = {
        ...estilosTabela,
        lineWidth: 0.05,
        fontSize: modoCompacto ? 6 : 7.5,
      };

      // Calcular larguras
      const larguraDisponivel = dimensoes.larguraPagina - (2 * dimensoes.espacamentoBordas);
      const larguraMestre = configuracaoBanda.mestre.colunas.reduce((sum, col) =>
        sum + (col.largura || 50), 0);

      let larguraMaxima = larguraMestre;
      if (configuracaoBanda.detalhes) {
        configuracaoBanda.detalhes.forEach(banda => {
          const larguraBanda = banda.colunas.reduce((sum, col) =>
            sum + (col.largura || 50), 0);
          larguraMaxima = Math.max(larguraMaxima, larguraBanda);
        });
      }

      const larguraFinal = Math.min(larguraMaxima, larguraDisponivel);
      const margemEsquerda = (dimensoes.larguraPagina - larguraFinal) / 2;

      const ajustarLarguras = (colunas: Coluna[]): { [key: string]: Partial<Styles> } => {
        if (larguraMaxima <= larguraDisponivel) {
          const numColunas = colunas.length;
          const larguraBase = colunas.slice(0, -1).reduce((sum, col) => sum + (col.largura || 50), 0);
          const larguraUltima = larguraFinal - larguraBase;

          return colunas.reduce((acc, col, idx) => ({
            ...acc,
            [idx.toString()]: {
              halign: col.alinhamento || 'left',
              cellWidth: idx === numColunas - 1 ? larguraUltima : (col.largura || 50),
            }
          }), {});
        }

        const fatorAjuste = larguraFinal / larguraMaxima;
        const numColunas = colunas.length;
        const larguraBase = colunas.slice(0, -1).reduce((sum, col) =>
          sum + ((col.largura || 50) * fatorAjuste), 0);
        const larguraUltima = larguraFinal - larguraBase;

        return colunas.reduce((acc, col, idx) => ({
          ...acc,
          [idx.toString()]: {
            halign: col.alinhamento || 'left',
            cellWidth: idx === numColunas - 1 ? larguraUltima : (col.largura || 50) * fatorAjuste,
          }
        }), {});
      };

      const estimarAlturaRegistro = (registro: any, isNonMasterDetail: boolean, includeHeader: boolean): number => {
        let alturaTotal = dimensoes.alturaRegistroMestre;

        if (isNonMasterDetail && includeHeader) {
          alturaTotal += dimensoes.alturaCabecalho;
        }

        if (configuracaoBanda.mestre.totalizadores) {
          alturaTotal += (modoCompacto ? 5 : 6) * configuracaoBanda.mestre.totalizadores.length + dimensoes.espacamentoSeccao;
        }

        if (configuracaoBanda.detalhes) {
          configuracaoBanda.detalhes.forEach(banda => {
            const dadosDetalhe = registro[banda.propriedadeDados];
            if (!dadosDetalhe) return;

            const listaDados = Array.isArray(dadosDetalhe) ? dadosDetalhe : [dadosDetalhe];
            if (listaDados.length === 0) return;

            alturaTotal += dimensoes.alturaCabecalho + (modoCompacto ? 4 : 5);
            alturaTotal += dimensoes.alturaCabecalho;
            alturaTotal += listaDados.length * dimensoes.alturaLinha;

            if (banda.totalizadores) {
              alturaTotal += (modoCompacto ? 4 : 5) * banda.totalizadores.length + dimensoes.espacamentoSeccao;
            }
          });
        }

        alturaTotal += modoCompacto ? 4 : 6;
        return alturaTotal;
      };

      const temEspacoSuficiente = (alturaNecessaria: number, posYAtual: number): boolean => {
        const espacoRestante = dimensoes.alturaPagina - posYAtual - (modoCompacto ? 22 : 25);
        return alturaNecessaria <= espacoRestante;
      };

      // === FUNCAO PARA RENDERIZAR TOTALIZADORES ===
      const renderizarTotalizadores = (
        totalizadores: Totalizador[],
        dados: any[],
        posicaoY: number,
        margem: number,
        largura: number,
        isDetalhe: boolean = false,
      ): number => {
        const alturaItem = modoCompacto ? 5 : 6;
        const alturaBarra = alturaItem * totalizadores.length + (modoCompacto ? 2 : 3);
        const recuo = isDetalhe ? (modoCompacto ? 3 : 5) : 0;

        pdf.setFillColor(paleta.totalFundo);
        pdf.roundedRect(
          margem + recuo,
          posicaoY,
          largura - (recuo * 2),
          alturaBarra,
          1, 1, 'F'
        );

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(modoCompacto ? 7 : 8);
        pdf.setTextColor(paleta.totalTexto);

        totalizadores.forEach((total, idx) => {
          const label = total.label.toUpperCase();
          const valor = formatValue(total.calculo(dados));
          pdf.text(
            `${label}:  ${valor}`,
            margem + recuo + (modoCompacto ? 4 : 6),
            posicaoY + (modoCompacto ? 3.5 : 4) + (idx * alturaItem)
          );
        });

        return posicaoY + alturaBarra + dimensoes.espacamentoSeccao;
      };

      // === SUBTOTAL DE GRUPO (linha leve, alinhada a direita) ===
      // Visual mais discreto que a barra escura do total geral: uma linha fina
      // separadora e o(s) valor(es) em negrito alinhados a direita da tabela.
      const renderizarSubtotalGrupo = (
        totalizadores: Totalizador[],
        dados: any[],
        posicaoY: number,
        margem: number,
        largura: number,
      ): number => {
        let y = posicaoY + (modoCompacto ? 1.5 : 2);

        pdf.setDrawColor(paleta.bordaMestre);
        pdf.setLineWidth(0.2);
        pdf.line(margem, y, margem + largura, y);
        y += modoCompacto ? 3 : 3.8;

        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(modoCompacto ? 7 : 8);
        pdf.setTextColor(paleta.destaque);

        const alturaItem = modoCompacto ? 3.6 : 4.4;
        totalizadores.forEach((total) => {
          const texto = `${total.label}:  ${formatValue(total.calculo(dados))}`;
          pdf.text(texto, margem + largura - 1, y, { align: "right" });
          y += alturaItem;
        });

        return y + (modoCompacto ? 0.5 : 1);
      };

      // === CALLBACK DE ESTILOS CONDICIONAIS ===
      const criarDidParseCell = (dados: any[], colunas: Coluna[], obterIndex: (dataRowIndex: number) => number) => {
        return (data: CellHookData) => {
          if (data.section === "body" && data.row.index >= 0) {
            const realIndex = obterIndex(data.row.index);
            const rowData = dados[realIndex];
            if (!rowData) return;
            colunas.forEach((col, colIndex) => {
              if (col.estiloCondicional && data.column.index === colIndex) {
                const valor = data.cell.raw;
                const estilos = col.estiloCondicional(valor, rowData);
                if (estilos) {
                  Object.keys(data.row.cells).forEach((key) => {
                    data.row.cells[key].styles = {
                      ...data.row.cells[key].styles,
                      ...estilos,
                    };
                  });
                }
              }
            });
          }
        };
      };

      // === VERIFICAR TIPO DE RELATORIO ===
      const isNonMasterDetail = !configuracaoBanda.detalhes || configuracaoBanda.detalhes.length === 0;

      const cabecalhosMestre = configuracaoBanda.mestre.colunas.map(col => col.titulo);
      const dadosMestre = fonteDeDados.map(registro =>
        configuracaoBanda.mestre.colunas.map(col => {
          if (col.calculado) return formatValue(col.calculado(registro));
          return formatValue(getNestedProperty(registro, col.propriedade));
        })
      );

      const masterTableStyles = {
        styles: {
          ...estilosTabela,
          fillColor: paleta.fundo,
          textColor: paleta.primaria,
          lineColor: paleta.borda,
        },
        headStyles: {
          fillColor: paleta.destaque,
          textColor: "#FFFFFF",
          fontStyle: "bold" as FontStyle,
          fontSize: modoCompacto ? 7 : 8.5,
          lineColor: paleta.destaque,
        },
        alternateRowStyles: {
          fillColor: paleta.fundoAlternado,
        },
        columnStyles: ajustarLarguras(configuracaoBanda.mestre.colunas),
        margin: { left: margemEsquerda, right: margemEsquerda },
        tableWidth: larguraFinal,
      };

      if (agrupamento) {
        // === RELATORIO AGRUPADO (ex.: por banco) ===
        const grupos = agruparPorCampo(fonteDeDados, agrupamento.campoTitulo, agrupamento.tituloVazio);

        grupos.forEach((grupo) => {
          // Garante espaco minimo para cabecalho do grupo + cabecalho da tabela + 1 linha
          const alturaMinima =
            (modoCompacto ? 5 : 6) + dimensoes.alturaCabecalho + dimensoes.alturaLinha + (modoCompacto ? 4 : 6);
          if (!temEspacoSuficiente(alturaMinima, posY)) {
            pdf.addPage();
            posY = dimensoes.margem;
          }

          // Barra de cabecalho do grupo
          pdf.setFillColor(paleta.fundoTitulo);
          pdf.roundedRect(margemEsquerda, posY - 1, larguraFinal, modoCompacto ? 5 : 6, 0.5, 0.5, 'F');
          pdf.setDrawColor(paleta.bordaMestre);
          pdf.setLineWidth(0.15);
          pdf.line(margemEsquerda, posY - 1, margemEsquerda, posY + (modoCompacto ? 4 : 5));
          pdf.setFontSize(modoCompacto ? 7 : 8.5);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(paleta.destaque);
          pdf.text(
            `${grupo.titulo}  (${grupo.itens.length})`,
            margemEsquerda + (modoCompacto ? 3 : 4),
            posY + (modoCompacto ? 2 : 2.5)
          );
          posY += modoCompacto ? 4 : 5;

          // Tabela de lancamentos do grupo
          const linhasGrupo = grupo.itens.map((item) =>
            configuracaoBanda.mestre.colunas.map((col) => {
              if (col.calculado) return formatValue(col.calculado(item));
              return formatValue(getNestedProperty(item, col.propriedade));
            })
          );

          autoTable(pdf, {
            ...masterTableStyles,
            head: [cabecalhosMestre],
            body: linhasGrupo,
            startY: posY,
            theme: "striped",
            didParseCell: criarDidParseCell(grupo.itens, configuracaoBanda.mestre.colunas, (i) => i),
          });

          posY = (pdf as any).lastAutoTable.finalY + dimensoes.espacamentoSeccao;

          // Subtotal do grupo (linha leve, alinhada a direita)
          if (agrupamento.totalizadores && agrupamento.totalizadores.length > 0) {
            posY = renderizarSubtotalGrupo(
              agrupamento.totalizadores,
              grupo.itens,
              posY,
              margemEsquerda,
              larguraFinal,
            );
          }

          posY += modoCompacto ? 2 : 3;
        });
      } else if (isNonMasterDetail) {
        let needsHeader = true;

        if (modoCompacto) {
          const alturaNecessaria = estimarAlturaRegistro({}, true, true);

          if (!temEspacoSuficiente(alturaNecessaria, posY)) {
            pdf.addPage();
            posY = dimensoes.margem;
            needsHeader = true;
          }

          autoTable(pdf, {
            ...masterTableStyles,
            head: needsHeader ? [cabecalhosMestre] : [],
            body: dadosMestre,
            startY: posY,
            theme: "striped",
            didParseCell: criarDidParseCell(fonteDeDados, configuracaoBanda.mestre.colunas, (i) => i),
          });

          posY = (pdf as any).lastAutoTable.finalY + dimensoes.espacamentoSeccao + 1;

          if (configuracaoBanda.mestre.totalizadores) {
            posY = renderizarTotalizadores(
              configuracaoBanda.mestre.totalizadores,
              fonteDeDados,
              posY,
              margemEsquerda,
              larguraFinal,
            );
          }
        } else {
          fonteDeDados.forEach((registro, index) => {
            const alturaNecessaria = estimarAlturaRegistro(registro, true, needsHeader);

            if (!temEspacoSuficiente(alturaNecessaria, posY)) {
              pdf.addPage();
              posY = dimensoes.margem;
              needsHeader = true;
            }

            if (index > 0) {
              pdf.setDrawColor(paleta.borda);
              pdf.setLineWidth(0.2);
              pdf.line(margemEsquerda, posY, margemEsquerda + larguraFinal, posY);
              posY += modoCompacto ? 2 : 3;
              pdf.setLineWidth(0.1);
            }

            // Indicador de registro
            pdf.setFontSize(modoCompacto ? 6.5 : 8);
            pdf.setFont("helvetica", "bold");
            pdf.setTextColor(paleta.secundaria);
            pdf.text(
              `${index + 1}/${fonteDeDados.length}`,
              margemEsquerda + 1,
              posY + 2
            );
            posY += modoCompacto ? 3 : 4;

            autoTable(pdf, {
              ...masterTableStyles,
              head: needsHeader ? [cabecalhosMestre] : [],
              body: [dadosMestre[index]],
              startY: posY,
              theme: "striped",
              didParseCell: criarDidParseCell(fonteDeDados, configuracaoBanda.mestre.colunas, () => index),
            });

            posY = (pdf as any).lastAutoTable.finalY + dimensoes.espacamentoSeccao;
            needsHeader = false;

            if (configuracaoBanda.mestre.totalizadores && index === fonteDeDados.length - 1) {
              posY += 1;
              posY = renderizarTotalizadores(
                configuracaoBanda.mestre.totalizadores,
                fonteDeDados,
                posY,
                margemEsquerda,
                larguraFinal,
              );
            }
          });
        }
      } else {
        // === RELATORIO MESTRE-DETALHE ===
        fonteDeDados.forEach((registro, index) => {
          const alturaNecessaria = estimarAlturaRegistro(registro, false, true);

          if (!temEspacoSuficiente(alturaNecessaria, posY)) {
            pdf.addPage();
            posY = dimensoes.margem;
          }

          if (index > 0) {
            pdf.setDrawColor(paleta.borda);
            pdf.setLineWidth(0.3);
            pdf.line(margemEsquerda, posY, margemEsquerda + larguraFinal, posY);
            posY += modoCompacto ? 2 : 4;
            pdf.setLineWidth(0.1);
          }

          // Barra de registro mestre
          pdf.setFillColor(paleta.fundoTitulo);
          pdf.roundedRect(
            margemEsquerda,
            posY - 1,
            larguraFinal,
            modoCompacto ? 5 : 6,
            0.5, 0.5, 'F'
          );
          pdf.setDrawColor(paleta.bordaMestre);
          pdf.setLineWidth(0.15);
          pdf.line(margemEsquerda, posY - 1, margemEsquerda, posY + (modoCompacto ? 4 : 5));

          pdf.setFontSize(modoCompacto ? 7 : 8.5);
          pdf.setFont("helvetica", "bold");
          pdf.setTextColor(paleta.destaque);
          pdf.text(
            `Registro ${index + 1} de ${fonteDeDados.length}`,
            margemEsquerda + (modoCompacto ? 3 : 4),
            posY + (modoCompacto ? 2 : 2.5)
          );

          posY += modoCompacto ? 4 : 5;

          autoTable(pdf, {
            ...masterTableStyles,
            head: [cabecalhosMestre],
            body: [dadosMestre[index]],
            startY: posY,
            theme: "striped",
            didParseCell: criarDidParseCell(fonteDeDados, configuracaoBanda.mestre.colunas, () => index),
          });

          posY = (pdf as any).lastAutoTable.finalY + dimensoes.espacamentoSeccao;

          if (configuracaoBanda.mestre.totalizadores) {
            posY += 1;
            posY = renderizarTotalizadores(
              configuracaoBanda.mestre.totalizadores,
              fonteDeDados,
              posY,
              margemEsquerda,
              larguraFinal,
            );
          }

          if (configuracaoBanda.detalhes) {
            configuracaoBanda.detalhes.forEach(banda => {
              const dadosDetalhe = registro[banda.propriedadeDados];
              if (!dadosDetalhe) return;

              const listaDados = Array.isArray(dadosDetalhe) ? dadosDetalhe : [dadosDetalhe];
              if (listaDados.length === 0) return;

              // Titulo da banda de detalhe
              pdf.setFillColor(paleta.fundoTitulo);
              pdf.roundedRect(
                margemEsquerda,
                posY,
                larguraFinal,
                modoCompacto ? 5 : 6,
                0.5, 0.5, 'F'
              );

              pdf.setFontSize(modoCompacto ? 6.5 : 8);
              pdf.setFont("helvetica", "bold");
              pdf.setTextColor(paleta.destaque);
              pdf.text(
                `${banda.nome}  (${listaDados.length})`,
                margemEsquerda + (modoCompacto ? 3 : 4),
                posY + (modoCompacto ? 3 : 3.5)
              );
              posY += modoCompacto ? 5 : 6;

              const cabecalhosDetalhe = banda.colunas.map(col => col.titulo);
              const linhasDetalhe = listaDados.map(item =>
                banda.colunas.map(col => {
                  if (col.calculado) return formatValue(col.calculado(item));
                  return formatValue(getNestedProperty(item, col.propriedade));
                })
              );

              autoTable(pdf, {
                head: [cabecalhosDetalhe],
                body: linhasDetalhe,
                startY: posY,
                theme: "striped",
                styles: estilosDetalhe,
                headStyles: {
                  fillColor: paleta.destaqueClaro,
                  textColor: "#FFFFFF",
                  fontStyle: "bold" as FontStyle,
                  fontSize: modoCompacto ? 6 : 7.5,
                  lineColor: paleta.destaqueClaro,
                },
                alternateRowStyles: {
                  fillColor: paleta.fundoAlternado,
                },
                columnStyles: ajustarLarguras(banda.colunas),
                margin: { left: margemEsquerda, right: margemEsquerda },
                tableWidth: larguraFinal,
                didParseCell: criarDidParseCell(listaDados, banda.colunas, (i) => i),
              });

              posY = (pdf as any).lastAutoTable.finalY + dimensoes.espacamentoSeccao;

              if (banda.totalizadores) {
                posY += 1;
                posY = renderizarTotalizadores(
                  banda.totalizadores,
                  listaDados,
                  posY,
                  margemEsquerda,
                  larguraFinal,
                  true,
                );
              }
            });
          }

          posY += modoCompacto ? 2 : 3;
        });
      }

      const pdfBlob = pdf.output("blob");
      const blobUrl = URL.createObjectURL(pdfBlob);
      const id = Date.now().toString();
      localStorage.setItem(`pdfURL_${id}`, blobUrl);
      window.open(`/pdf-viewer?id=${id}`, "_blank");
    };

    img.crossOrigin = "anonymous";
    img.onload = () => gerarConteudoPDF(img);
    img.onerror = () => {
      console.warn("Falha ao carregar logo, gerando PDF sem logo.");
      gerarConteudoPDF(null);
    };
    img.src = url || logo;
  };

  const sanitizarNomeSheet = (nome: string): string => {
    // Excel: máximo 31 chars, sem :\/?*[]
    const limpo = (nome || 'Dados').replace(/[:\\/?*\[\]]/g, '').trim();
    return limpo.substring(0, 31) || 'Dados';
  };

  const sanitizarNomeArquivo = (nome: string): string => {
    return nome.replace(/[^a-zA-Z0-9-_]/g, '_').replace(/_+/g, '_').replace(/^_+|_+$/g, '');
  };

  const gerarRelatorioXLSX = () => {
    const wb = XLSX.utils.book_new();

    // === RELATORIO AGRUPADO (ex.: por banco) ===
    // Uma unica planilha com, por grupo: titulo, cabecalho, linhas, subtotal e
    // linha em branco. Ao final, o total geral (rodape).
    if (agrupamento) {
      const grupos = agruparPorCampo(fonteDeDados, agrupamento.campoTitulo, agrupamento.tituloVazio);
      const headersAgrupado = configuracaoBanda.mestre.colunas.map(c => c.titulo);

      const aoa: any[][] = [
        [titulo],
        [empresa],
      ];
      if (subtitulo) aoa.push([subtitulo]);
      aoa.push([`Emitido por ${usuario} em ${new Date().toLocaleString('pt-BR')}`]);
      aoa.push([]);

      grupos.forEach(grupo => {
        aoa.push([`${grupo.titulo} (${grupo.itens.length})`]);
        aoa.push(headersAgrupado);
        grupo.itens.forEach(item => {
          aoa.push(
            configuracaoBanda.mestre.colunas.map(col => {
              const valor = col.calculado
                ? col.calculado(item)
                : getNestedProperty(item, col.propriedade);
              return valor ?? '';
            })
          );
        });
        if (agrupamento.totalizadores && agrupamento.totalizadores.length > 0) {
          agrupamento.totalizadores.forEach(t => {
            aoa.push([`${t.label}:`, t.calculo(grupo.itens)]);
          });
        }
        aoa.push([]);
      });

      // Total geral (mesmas linhas exibidas no rodape do PDF)
      rodape.forEach(linha => aoa.push([linha]));

      const wsAgrupado = XLSX.utils.aoa_to_sheet(aoa);
      XLSX.utils.book_append_sheet(wb, wsAgrupado, sanitizarNomeSheet('Dados'));

      const dataStrAgrupado = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
      const nomeArquivoAgrupado = `${sanitizarNomeArquivo(titulo)}_${dataStrAgrupado}.xlsx`;
      XLSX.writeFile(wb, nomeArquivoAgrupado);
      return;
    }

    const linhasMestre = fonteDeDados.map(registro => {
      const linha: Record<string, any> = {};
      configuracaoBanda.mestre.colunas.forEach(col => {
        const valor = col.calculado
          ? col.calculado(registro)
          : getNestedProperty(registro, col.propriedade);
        linha[col.titulo] = valor ?? '';
      });
      return linha;
    });

    // Adiciona totalizadores como linha extra abaixo da tabela mestre quando houver
    const linhasMestreComTotais = [...linhasMestre];
    if (configuracaoBanda.mestre.totalizadores && configuracaoBanda.mestre.totalizadores.length > 0 && linhasMestre.length > 0) {
      linhasMestreComTotais.push({}); // linha em branco
      configuracaoBanda.mestre.totalizadores.forEach(t => {
        const linhaTotal: Record<string, any> = {};
        const primeiraColuna = configuracaoBanda.mestre.colunas[0]?.titulo || 'Total';
        linhaTotal[primeiraColuna] = `${t.label}:`;
        const segundaColuna = configuracaoBanda.mestre.colunas[1]?.titulo;
        if (segundaColuna) linhaTotal[segundaColuna] = t.calculo(fonteDeDados);
        linhasMestreComTotais.push(linhaTotal);
      });
    }

    const isMestreDetalhe = configuracaoBanda.detalhes && configuracaoBanda.detalhes.length > 0;
    const nomeAbaMestre = isMestreDetalhe ? 'Resumo' : 'Dados';

    // Cabeçalho com metadados (título, empresa, filtros, usuário/data)
    const metaInfo: any[][] = [
      [titulo],
      [empresa],
    ];
    if (subtitulo) metaInfo.push([subtitulo]);
    metaInfo.push([`Emitido por ${usuario} em ${new Date().toLocaleString('pt-BR')}`]);
    metaInfo.push([]);

    const headers = configuracaoBanda.mestre.colunas.map(c => c.titulo);
    const wsMestre = XLSX.utils.aoa_to_sheet(metaInfo);
    XLSX.utils.sheet_add_json(wsMestre, linhasMestreComTotais, {
      header: headers,
      origin: -1, // append após o cabeçalho de metadados
    });
    XLSX.utils.book_append_sheet(wb, wsMestre, sanitizarNomeSheet(nomeAbaMestre));

    // Para mestre-detalhe: uma sheet por banda de detalhe, com coluna de referência ao registro pai
    if (isMestreDetalhe && configuracaoBanda.detalhes) {
      const colunaIdentificadora = configuracaoBanda.mestre.colunas[0];
      const labelRegistroPai = colunaIdentificadora?.titulo || 'Registro';

      configuracaoBanda.detalhes.forEach(banda => {
        const linhasBanda: Record<string, any>[] = [];

        fonteDeDados.forEach(registro => {
          const dadosDetalhe = registro[banda.propriedadeDados];
          if (!dadosDetalhe) return;
          const lista = Array.isArray(dadosDetalhe) ? dadosDetalhe : [dadosDetalhe];
          if (lista.length === 0) return;

          const idPai = colunaIdentificadora
            ? (colunaIdentificadora.calculado
                ? colunaIdentificadora.calculado(registro)
                : getNestedProperty(registro, colunaIdentificadora.propriedade))
            : '';

          lista.forEach(item => {
            const linha: Record<string, any> = {};
            linha[labelRegistroPai] = idPai ?? '';
            banda.colunas.forEach(col => {
              const valor = col.calculado
                ? col.calculado(item)
                : getNestedProperty(item, col.propriedade);
              linha[col.titulo] = valor ?? '';
            });
            linhasBanda.push(linha);
          });
        });

        if (linhasBanda.length === 0) return;

        // Totalizadores da banda como linhas finais
        if (banda.totalizadores && banda.totalizadores.length > 0) {
          // Reunir todos os itens da banda para totalizar
          const todosItens: any[] = [];
          fonteDeDados.forEach(registro => {
            const dados = registro[banda.propriedadeDados];
            if (dados) {
              if (Array.isArray(dados)) todosItens.push(...dados);
              else todosItens.push(dados);
            }
          });
          linhasBanda.push({});
          banda.totalizadores.forEach(t => {
            const linhaTotal: Record<string, any> = {};
            linhaTotal[labelRegistroPai] = `${t.label}:`;
            const primeiraColunaBanda = banda.colunas[0]?.titulo;
            if (primeiraColunaBanda) linhaTotal[primeiraColunaBanda] = t.calculo(todosItens);
            linhasBanda.push(linhaTotal);
          });
        }

        const headersBanda = [labelRegistroPai, ...banda.colunas.map(c => c.titulo)];
        const wsBanda = XLSX.utils.json_to_sheet(linhasBanda, { header: headersBanda });
        XLSX.utils.book_append_sheet(wb, wsBanda, sanitizarNomeSheet(banda.nome));
      });
    }

    const dataStr = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
    const nomeArquivo = `${sanitizarNomeArquivo(titulo)}_${dataStr}.xlsx`;
    XLSX.writeFile(wb, nomeArquivo);
  };

  useEffect(() => {
    if (trigger > 0) {
      if (formato === 'xlsx') {
        gerarRelatorioXLSX();
      } else {
        gerarRelatorioPDF();
      }
    }
  }, [trigger]);

  return null;
};
